import { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { adBreakPoint } from '../../lib/helpers';
import {
  changeMediaFilterClientPersistent,
  selectMenuActive,
  selectSettingsMenuState,
  selectUploadFormActive,
} from '../../ducks';
import { increaseView } from '../../apis/dumpert';
import { matchPath, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { LIST_PAGE_PATH, getValidItemId, ITEM_PAGE_PATH } from '../paths';
import { normalizeMediaFilterPath } from '../../history';
import { cookieToPath } from '../../cookie';
import { Device } from '../../device';

export const useMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted;
};

export const useShowingOnScroll = () => {
  const [showing, setShowing] = useState(true);
  const { getState } = useStore();
  let prevScrollpos = 0;

  const onScroll = () => {
    const currentScrollPos = window.pageYOffset;

    const state = getState();
    if (adBreakPoint(window.innerWidth) !== 'XS' ||
      selectMenuActive(state) ||
      selectSettingsMenuState(state) ||
      selectUploadFormActive(state)) {
      setShowing(true);
    } else {
      setShowing(currentScrollPos > 50 ? prevScrollpos > currentScrollPos : true);
    }
    prevScrollpos = currentScrollPos;
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return showing;
};

export const useCurrentBreakPoint = () => {
  const initialWidth = () => {
    if (Device.IS_TABLET) { return 640; }
    if (Device.IS_DESKTOP) { return 1200; }
    return 0;
  };

  const [currentBreakPoint, setCurrentBreakPoint] = useState(initialWidth());

  const onResize = () => setCurrentBreakPoint(window.innerWidth);

  useEffect(() => {
    setCurrentBreakPoint(window.innerWidth);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return currentBreakPoint;
};

export const useIncreaseItemViews = itemId => {
  useEffect(() => {
    increaseView(itemId)
      .then(() => null)
      .catch(() => { /* no-op */ });
    try {
      const key = 'viewedItems';
      const viewed = (window.sessionStorage.getItem(key) || '').split(',');
      window.sessionStorage.setItem(key, [...new Set([...viewed, itemId])].slice(-100).join(','));
    } catch (e) {

      // no-op
    }
  }, [itemId]);
};

export const useChangeMediaFilter = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  return filterValue => {
    dispatch(changeMediaFilterClientPersistent(filterValue));
    const { pathname } = history.location;
    const match = matchPath(pathname, { path: LIST_PAGE_PATH, exact: true });
    if(match) {
      const { mediaFilter } = match.params;
      const pathValue = cookieToPath(filterValue);
      if (mediaFilter !== pathValue) {
        normalizeMediaFilterPath(history, pathValue,  match);
      }
    }
  };
};

export const useItemViewSearchParams = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return {
    selectedId: getValidItemId(params.get('selectedId')),
  };
};

/**
 * To know whether current page is item details view or not
 * @returns item id or undefined
 */
export const useItemIdParam = () => {
  const { selectedId } = useItemViewSearchParams();
  const { itemId } = (useRouteMatch(ITEM_PAGE_PATH) || {}).params || {};
  return selectedId || itemId;
};

export const currentActiveMenuLocation = () => {
  const { pathname } = useLocation();
  switch (pathname) {
  case '/':
  case '/latest':
    return 'nieuw';
  case '/toppers':
  case '/top/dag/':
  case '/top/week/':
  case '/top/maand/':
    return 'toppers';
  case '/tag/dumperttv':
    return 'dumperttv';
  }
};

export const copyTextToClipboard = (text, func) => {
  const textField = document.createElement('textarea');
  textField.innerText = text;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand('copy');
  textField.remove();
  func();
};

export const useQueryCommandIsSupported = () => {
  const [isSupported, setIsSupported] = useState(false);
  useEffect(() => {
    if (document.queryCommandSupported('copy')) {
      setIsSupported(true);
    }
  }, []);
  return isSupported;
};

export const useDidomiReady = () => {
  const [didomiReady, setDidomiReady] = useState(false);
  useEffect(() => {
    window.didomiOnReady = window.didomiOnReady || [];
    window.didomiOnReady.push(() => {
      setDidomiReady(true);
    });
  }, []);
  return didomiReady;
};
